// OrderForm.jsx - UPDATED with array display support
import React, { useState, useMemo } from 'react';
import './OrderForm.css';

const OrderForm = ({ product, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Get image URL helper
  const getImageUrl = (img) => {
    if (!img) return '/placeholder-image.jpg';
    if (typeof img === 'object' && img.url) return img.url;
    if (typeof img === 'string') return img;
    return '/placeholder-image.jpg';
  };

  // Get primary product image
  const getProductImage = () => {
    if (Array.isArray(product.images) && product.images.length > 0) {
      return getImageUrl(product.images[0]);
    }
    if (product.image) {
      return getImageUrl(product.image);
    }
    return '/placeholder-image.jpg';
  };

  const [selectedVariantIndex, setSelectedVariantIndex] = useState(
    product?.selectedVariantIndex ?? 0
  );

  const selectedVariant = useMemo(() => {
    if (product?.hasVariants && product?.variants && product.variants[selectedVariantIndex]) {
      return product.variants[selectedVariantIndex];
    }
    return product?.selectedVariant || null;
  }, [selectedVariantIndex, product]);

  const currentPrice = useMemo(() => {
    return selectedVariant?.price || product?.price || 0;
  }, [selectedVariant, product]);

  // Helper function to create variant details object - UPDATED for arrays
  const createVariantDetails = (variant) => {
    if (!variant) return null;

    const details = {};

    if (variant.name) details.name = variant.name;
    if (variant.price !== undefined) details.price = variant.price;
    
    // UPDATED: Handle both array and single values
    if (Array.isArray(variant.colors) && variant.colors.length > 0) {
      details.colors = variant.colors;
    } else if (variant.color) {
      details.color = variant.color;
    }
    
    if (Array.isArray(variant.materials) && variant.materials.length > 0) {
      details.materials = variant.materials;
    } else if (variant.material) {
      details.material = variant.material;
    }
    
    if (variant.compartiment) details.compartiment = variant.compartiment;

    if (variant.dimensions && typeof variant.dimensions === 'object') {
      const dims = {};
      if (variant.dimensions.width) dims.width = variant.dimensions.width;
      if (variant.dimensions.height) dims.height = variant.dimensions.height;
      if (variant.dimensions.length) dims.length = variant.dimensions.length;

      if (Object.keys(dims).length > 0) {
        details.dimensions = dims;
      }
    }

    return Object.keys(details).length > 0 ? details : null;
  };

  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    wilaya: '',
    commune: '',
    address: '',
    productId: product?.id || '',
    productName: product?.name || '',
    productPrice: currentPrice,
    productImage: getProductImage(),
    quantity: product?.quantity || 1,
    notes: '',
    orderDate: new Date().toISOString(),
    status: 'pending',
    hasVariants: product?.hasVariants || false,
    selectedVariant: selectedVariant,
    variantDetails: createVariantDetails(selectedVariant)
  });

  const [errors, setErrors] = useState({});

  const wilayas = [
    'Adrar', 'Chlef', 'Laghouat', 'Oum El Bouaghi', 'Batna', 'Béjaïa', 'Biskra',
    'Béchar', 'Blida', 'Bouira', 'Tamanrasset', 'Tébessa', 'Tlemcen', 'Tiaret',
    'Tizi Ouzou', 'Alger', 'Djelfa', 'Jijel', 'Sétif', 'Saïda', 'Skikda',
    'Sidi Bel Abbès', 'Annaba', 'Guelma', 'Constantine', 'Médéa', 'Mostaganem',
    'M\'Sila', 'Mascara', 'Ouargla', 'Oran', 'El Bayadh', 'Illizi', 'Bordj Bou Arreridj',
    'Boumerdès', 'El Tarf', 'Tindouf', 'Tissemsilt', 'El Oued', 'Khenchela',
    'Souk Ahras', 'Tipaza', 'Mila', 'Aïn Defla', 'Naâma', 'Aïn Témouchent',
    'Ghardaïa', 'Relizane'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Update form when variant changes
  React.useEffect(() => {
    setFormData(prev => ({
      ...prev,
      productPrice: currentPrice,
      selectedVariant: selectedVariant,
      variantDetails: createVariantDetails(selectedVariant)
    }));
  }, [selectedVariant, currentPrice]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Le nom est requis';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Le téléphone est requis';
    } else if (!/^(0)(5|6|7)[0-9]{8}$/.test(formData.phone)) {
      newErrors.phone = 'Numéro invalide (ex: 0555123456)';
    }
    if (!formData.wilaya) {
      newErrors.wilaya = 'La wilaya est requise';
    }
    if (!formData.commune.trim()) {
      newErrors.commune = 'La commune est requise';
    }
    if (!formData.address.trim()) {
      newErrors.address = 'L\'adresse est requise';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      const total = formData.productPrice * formData.quantity;

      // Create clean order data without undefined values
      const orderData = {
        // Customer info
        fullName: formData.fullName,
        phone: formData.phone,
        wilaya: formData.wilaya,
        commune: formData.commune,
        address: formData.address,

        // Product info
        productId: formData.productId,
        productName: formData.productName,
        productPrice: formData.productPrice,
        productImage: formData.productImage,
        quantity: formData.quantity,
        total: total,

        // Order info
        orderNumber: `ORD-${Date.now()}`,
        status: formData.status,
        orderDate: formData.orderDate,
        createdAt: new Date().toISOString(),

        // Variant info (only if exists)
        hasVariants: formData.hasVariants
      };

      // Only add optional fields if they have values
      if (formData.email && formData.email.trim()) {
        orderData.email = formData.email;
      }

      if (formData.notes && formData.notes.trim()) {
        orderData.notes = formData.notes;
      }

      // Only add variant details if they exist
      if (formData.variantDetails) {
        orderData.variantDetails = formData.variantDetails;
      }

      const { db } = await import('../../../services/firebase/firebaseconfig');
      const { collection, addDoc } = await import('firebase/firestore');

      const ordersRef = collection(db, 'orders');
      await addDoc(ordersRef, orderData);

      setShowModal(true);

      setTimeout(() => {
        setShowModal(false);
        onClose?.();
      }, 5000);

    } catch (error) {
      console.error('Error submitting order:', error);
      alert('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return price?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') || '0';
  };

  const formatDimensions = (dimensions) => {
    if (!dimensions) return '';
    const parts = [];
    if (dimensions.width) parts.push(`L${dimensions.width}`);
    if (dimensions.height) parts.push(`H${dimensions.height}`);
    if (dimensions.length) parts.push(`P${dimensions.length}`);
    return parts.join(' × ') + ' cm';
  };

  const getColorStyle = (color) => {
    const colorMap = {
      'noir': '#000000',
      'black': '#000000',
      'blanc': '#FFFFFF',
      'white': '#FFFFFF',
      'rouge': '#EF4444',
      'red': '#EF4444',
      'bleu': '#3B82F6',
      'blue': '#3B82F6',
      'vert': '#22C55E',
      'green': '#22C55E',
      'jaune': '#FBBF24',
      'yellow': '#FBBF24',
      'gris': '#9CA3AF',
      'gray': '#9CA3AF',
      'grey': '#9CA3AF',
      'marron': '#8B4513',
      'brown': '#8B4513',
      'rose': '#EC4899',
      'pink': '#EC4899',
      'orange': '#F97316',
      'violet': '#A855F7',
      'purple': '#A855F7',
      'turquoise': '#14B8A6',
      'teal': '#14B8A6',
    };
    return colorMap[color?.toLowerCase()] || '#D1D5DB';
  };

  const getMaterialIcon = (material) => {
    const materialIcons = {
      'cuir': '🧴',
      'leather': '🧴',
      'tissu': '🧵',
      'fabric': '🧵',
      'bois': '🪵',
      'wood': '🪵',
      'métal': '⚙️',
      'metal': '⚙️',
      'plastique': '📲',
      'plastic': '📲',
      'coton': '🧵',
      'cotton': '🧵',
      'lin': '🧵',
      'linen': '🧵',
      'soie': '✨',
      'silk': '✨',
    };
    return materialIcons[material?.toLowerCase()] || '📦';
  };

  // UPDATED: Get unique colors from variants (handle arrays)
  const uniqueColors = useMemo(() => {
    if (!product?.variants) return [];
    const colors = new Set();
    product.variants.forEach(v => {
      if (Array.isArray(v.colors)) {
        v.colors.forEach(c => colors.add(c));
      } else if (v.color) {
        colors.add(v.color);
      }
    });
    return Array.from(colors);
  }, [product]);

  // UPDATED: Get unique materials from variants (handle arrays)
  const uniqueMaterials = useMemo(() => {
    if (!product?.variants) return [];
    const materials = new Set();
    product.variants.forEach(v => {
      if (Array.isArray(v.materials)) {
        v.materials.forEach(m => materials.add(m));
      } else if (v.material) {
        materials.add(v.material);
      }
    });
    return Array.from(materials);
  }, [product]);

  return (
    <>
      <div className="ofrm-card">
        <div className="ofrm-card-header">
          <h2 className="ofrm-card-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="9" cy="21" r="1" />
              <circle cx="20" cy="21" r="1" />
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
            </svg>
            Passer la commande
          </h2>
          <p className="ofrm-card-subtitle">Remplissez le formulaire pour finaliser votre achat</p>
        </div>

        <div className="ofrm-card-content">
          <form onSubmit={handleSubmit}>
            {/* Product Summary */}
            <div className="ofrm-product-summary">
              <img src={formData.productImage} alt={formData.productName} className="ofrm-product-img" />
              <div className="ofrm-product-info">
                <h4 className="ofrm-product-name">{formData.productName}</h4>
                <p className="ofrm-product-price">{formatPrice(formData.productPrice)} DA</p>

                {/* UPDATED: Variant Details with array support */}
                {formData.hasVariants && formData.variantDetails && (
                  <div className="ofrm-variant-info">
                    {formData.variantDetails.dimensions && (
                      <span className="ofrm-variant-tag">
                        📏 {formatDimensions(formData.variantDetails.dimensions)}
                      </span>
                    )}
                    {/* UPDATED: Display multiple colors */}
                    {formData.variantDetails.colors && formData.variantDetails.colors.length > 0 && (
                      <span className="ofrm-variant-tag">
                        🎨 {formData.variantDetails.colors.join(' + ')}
                      </span>
                    )}
                    {/* Backward compatibility: single color */}
                    {formData.variantDetails.color && !formData.variantDetails.colors && (
                      <span className="ofrm-variant-tag">
                        🎨 {formData.variantDetails.color}
                      </span>
                    )}
                    {/* UPDATED: Display multiple materials */}
                    {formData.variantDetails.materials && formData.variantDetails.materials.length > 0 && (
                      <span className="ofrm-variant-tag">
                        🔨 {formData.variantDetails.materials.join(' + ')}
                      </span>
                    )}
                    {/* Backward compatibility: single material */}
                    {formData.variantDetails.material && !formData.variantDetails.materials && (
                      <span className="ofrm-variant-tag">
                        🔨 {formData.variantDetails.material}
                      </span>
                    )}
                    {formData.variantDetails.compartiment && (
                      <span className="ofrm-variant-tag">
                        📦 {formData.variantDetails.compartiment}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Personal Information */}
            <div className="ofrm-section">
              <h3 className="ofrm-section-title">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                Informations personnelles
              </h3>
              <div className="ofrm-grid">
                <div className="ofrm-form-group">
                  <label className="ofrm-label">Nom complet *</label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className={`ofrm-input ${errors.fullName ? 'error' : ''}`}
                    placeholder="Ahmed Benali"
                  />
                  {errors.fullName && <span className="ofrm-error-msg">{errors.fullName}</span>}
                </div>

                <div className="ofrm-form-group">
                  <label className="ofrm-label">Téléphone *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={`ofrm-input ${errors.phone ? 'error' : ''}`}
                    placeholder="0555123456"
                  />
                  {errors.phone && <span className="ofrm-error-msg">{errors.phone}</span>}
                </div>

                <div className="ofrm-form-group full">
                  <label className="ofrm-label">Email (optionnel)</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="ofrm-input"
                    placeholder="exemple@email.com"
                  />
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            <div className="ofrm-section">
              <h3 className="ofrm-section-title">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                Adresse de livraison
              </h3>
              <div className="ofrm-grid">
                <div className="ofrm-form-group">
                  <label className="ofrm-label">Wilaya *</label>
                  <select
                    name="wilaya"
                    value={formData.wilaya}
                    onChange={handleInputChange}
                    className={`ofrm-select ${errors.wilaya ? 'error' : ''}`}
                  >
                    <option value="">Sélectionner</option>
                    {wilayas.map(wilaya => (
                      <option key={wilaya} value={wilaya}>{wilaya}</option>
                    ))}
                  </select>
                  {errors.wilaya && <span className="ofrm-error-msg">{errors.wilaya}</span>}
                </div>

                <div className="ofrm-form-group">
                  <label className="ofrm-label">Commune *</label>
                  <input
                    type="text"
                    name="commune"
                    value={formData.commune}
                    onChange={handleInputChange}
                    className={`ofrm-input ${errors.commune ? 'error' : ''}`}
                    placeholder="Bab Ezzouar"
                  />
                  {errors.commune && <span className="ofrm-error-msg">{errors.commune}</span>}
                </div>

                <div className="ofrm-form-group full">
                  <label className="ofrm-label">Adresse complète *</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className={`ofrm-textarea ${errors.address ? 'error' : ''}`}
                    placeholder="Rue, Bâtiment, Appartement..."
                  />
                  {errors.address && <span className="ofrm-error-msg">{errors.address}</span>}
                </div>
              </div>
            </div>

            {/* Order Details */}
            <div className="ofrm-section">
              <h3 className="ofrm-section-title">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="12" y1="18" x2="12" y2="12" />
                  <line x1="9" y1="15" x2="15" y2="15" />
                </svg>
                Détails de la commande
              </h3>
              <div className="ofrm-grid">
                <div className="ofrm-form-group">
                  <label className="ofrm-label">Quantité</label>
                  <input
                    type="number"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleInputChange}
                    className="ofrm-input"
                    min="1"
                    max="10"
                  />
                </div>

                <div className="ofrm-form-group full">
                  <label className="ofrm-label">Notes (optionnel)</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    className="ofrm-textarea"
                    placeholder="Instructions spéciales..."
                  />
                </div>
              </div>
            </div>

            {/* Variant Selection at Bottom */}
            {product?.hasVariants && product?.variants && product.variants.length > 0 && (
              <div className="ofrm-variant-section">
                <h3 className="ofrm-section-title" style={{ marginBottom: '16px' }}>
                  Sélectionner une variante
                </h3>

                {/* Display variant cards */}
                <div className="ofrm-variant-cards">
                  {product.variants.map((variant, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className={`ofrm-variant-card-btn ${selectedVariantIndex === idx ? 'active' : ''}`}
                      onClick={() => setSelectedVariantIndex(idx)}
                    >
                      <div className="ofrm-variant-card-content">
                        <div className="ofrm-variant-card-name">{variant.name}</div>
                        <div className="ofrm-variant-card-details">
                          {variant.dimensions && (
                            <span className="ofrm-variant-detail-item">
                              📏 {formatDimensions(variant.dimensions)}
                            </span>
                          )}
                          {/* UPDATED: Display multiple colors */}
                          {Array.isArray(variant.colors) && variant.colors.length > 0 && (
                            <span className="ofrm-variant-detail-item">
                              🎨 {variant.colors.join(' + ')}
                            </span>
                          )}
                          {variant.color && !variant.colors && (
                            <span className="ofrm-variant-detail-item">
                              🎨 {variant.color}
                            </span>
                          )}
                          {/* UPDATED: Display multiple materials */}
                          {Array.isArray(variant.materials) && variant.materials.length > 0 && (
                            <span className="ofrm-variant-detail-item">
                              🔨 {variant.materials.join(' + ')}
                            </span>
                          )}
                          {variant.material && !variant.materials && (
                            <span className="ofrm-variant-detail-item">
                              🔨 {variant.material}
                            </span>
                          )}
                          {variant.compartiment && (
                            <span className="ofrm-variant-detail-item">
                              📦 {variant.compartiment}
                            </span>
                          )}
                        </div>
                        <div className="ofrm-variant-card-price">
                          {formatPrice(variant.price)} DA
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Order Total */}
            <div className="ofrm-total-box">
              <div className="ofrm-total-row">
                <span>Sous-total:</span>
                <span>{formatPrice(formData.productPrice * formData.quantity)} DA</span>
              </div>
              <div className="ofrm-total-row">
                <span>Livraison:</span>
                <span className="ofrm-free-ship">Disponible</span>
              </div>
              <div className="ofrm-total-row total">
                <span>Total:</span>
                <span>{formatPrice(formData.productPrice * formData.quantity)} DA</span>
              </div>
            </div>

            {/* Submit Button */}
            <button type="submit" className="ofrm-btn-submit" disabled={loading}>
              {loading ? (
                <>
                  <span className="ofrm-spinner"></span>
                  Envoi en cours...
                </>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Confirmer la commande
                </>
              )}
            </button>
            <br />
            {/* WhatsApp Button */}
            <button
              type="button"
              className="pdp-btn-whatsapp"
              onClick={() => {
                const message = `Bonjour, je suis intéressé par le produit:\n${formData.productName}\nPrix: ${formatPrice(formData.productPrice)} DA`;
                const whatsappUrl = `https://wa.me/213560605655?text=${encodeURIComponent(message)}`;
                window.open(whatsappUrl, '_blank');
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
              </svg>
              Commander via WhatsApp
            </button>
            <p className="ofrm-note">
              * En confirmant, vous acceptez nos conditions générales de vente
            </p>
            <br /><br /><br /><br /><br />
          </form>
        </div>
      </div>

      {/* Success Modal */}
      {showModal && (
        <div className="ofrm-success-overlay">
          <div className="ofrm-success-modal">
            <div className="ofrm-success-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2>Commande envoyée avec succès!</h2>
            <p>Nous vous contacterons bientôt pour confirmer votre commande.</p>
          </div>
        </div>
      )}
    </>
  );
};

export default OrderForm;